import React from "react";
import EbDialog from "../ExecutionBlueprintLiveReview/_shared/EbDialog";
import "./LiveReviewStepperModal.scss";

// ═════════════════════════════════════════════════════════════════════════════
// The confirmation in front of "Start Live Review" — the coach's last stop
// before the joint session begins. Figma node 33831:50065.
//
// ⚠️ REBUILT ON `EbDialog` (2026-08-31). Same props, same call site; the shell,
// the copy and the icon changed.
//
// What went, and why:
//
//   - **Its own 604px panel.** It was one of seven dialog shells across the two
//     apps. It is the `lg` size of the shared one now.
//   - **The info icon beside the title.** Removed on instruction. It was a
//     signifier with nothing behind it: no tooltip, no handler, nothing to
//     click. An icon that looks interactive and isn't is worse than no icon.
//   - **"Execution Blueprint Progress".** A dialog that appears when you press a
//     button should answer the question that button asked. It names the action
//     now, and the three stages become the ANSWER to "what am I starting?"
//     rather than a status board.
//
// What stayed: the three-stage list, which is the whole reason this is a dialog
// and not a plain confirm — a coach about to open a live session with a CEO
// needs to know it runs to three stages before they start, not discover the
// second one halfway through.
//
// The footer keeps `align="between"`, the one place in the system that does:
// this is genuinely a step forward in a sequence, so back-on-the-left reads
// correctly here where it would be noise on a yes/no.
// ═════════════════════════════════════════════════════════════════════════════

const STEPS = [
  {
    // ⚠️ NOT "the AI-generated blueprint" (2026-09-03). Stage 1 is the coach
    // and CEO working through what the COACH flagged in their solo pass — the
    // AI critique finished two stages earlier, back in the CEO's own journey.
    // Naming the AI here pointed the coach at the wrong artefact, and "refine
    // your objectives" described nothing that screen actually does.
    title: "Blueprint Review",
    text: "Work through what you flagged, sharpen the wording and finalise each WIG. Every WIG must be a clear outcome before you connect anything.",
  },
  {
    title: "WIG Connections",
    text: "The AI drafts a map of how the WIGs contribute to each other and to the BHAG. Adjust it with the CEO.",
  },
  {
    // ⚠️ "assign priorities" PROMISED A CONTROL THAT NO LONGER EXISTS. The
    // Year 1 screen's priority number was derived from row position, never a
    // server field, and the 2026-09-03 redesign removed it from the UI
    // entirely ("display shorthand and nothing more... no longer drawn"). This
    // modal was still telling the coach they'd be assigning priorities.
    //
    // "Up to five" is also deliberately absent: both source docs specify that
    // cap, but there is NO five-limit anywhere in the code — no max constant,
    // no enforcement, no counter. Promising a limit the next screen won't hold
    // teaches the coach the copy can't be trusted. If the cap gets built, this
    // line is where it should be said.
    title: "Prioritised WIGs for the next 12 months",
    // The AI proposes priorities and owners (YEAR1_DRAFTING); the lock is
    // joint and has no unlock path on the backend.
    text: "The AI proposes the 12-month priorities and an owner for each. Agree them with the CEO, then lock the blueprint. Locking can't be undone.",
  },
];

const PlayIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
    <path
      d="M10.2 8.9v6.2l5.1-3.1-5.1-3.1Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * @param activeStep  1-based. Only ever 1 today — the dialog is shown before
 *                    anything has started — but the list is written to mark any
 *                    step, so it survives being reused mid-session.
 * @param onNext      Starts the live review. The one-way action.
 * @param onBack      Dismisses. Also what Escape and the backdrop call.
 * @param nextLabel/backLabel  Button copy.
 * @param nextLoading The POST is in flight: spins the primary and locks
 *                    dismissal, so the dialog can't be closed out from under a
 *                    request that is about to change the blueprint's state.
 */
const LiveReviewStepperModal = ({
  activeStep = 1,
  onNext,
  onBack,
  nextLabel = "Start live review",
  backLabel = "Go back",
  nextLoading = false,
}) => (
  <EbDialog
    isOpen
    size="lg"
    tone="primary"
    icon={<PlayIcon />}
    title="Start the live review?"
    subtitle="You'll work through the blueprint with the CEO and their leadership team in three stages, then lock it together."
    busy={nextLoading}
    onClose={onBack}
    align="between"
    actions={[
      { label: backLabel, variant: "ghost", onClick: onBack },
      {
        label: nextLabel,
        loadingLabel: "Starting…",
        variant: "primary",
        onClick: onNext,
        loading: nextLoading,
        autoFocus: true,
      },
    ]}
  >
    <ol className="lrs-steps">
      {STEPS.map((step, index) => {
        const number = index + 1;
        const isActive = number === activeStep;
        return (
          <li
            className={`lrs-step${isActive ? " is-active" : ""}`}
            key={step.title}
          >
            <span className="lrs-step-rail">
              <span className="lrs-step-num">{number}</span>
              {/* No rail below the last one — it would trail off into nothing
                  and imply a fourth stage. */}
              {number < STEPS.length ? (
                <span className="lrs-step-line" />
              ) : null}
            </span>
            <div className="lrs-step-body">
              <p className="lrs-step-title">{step.title}</p>
              <p className="lrs-step-text">{step.text}</p>
            </div>
          </li>
        );
      })}
    </ol>
  </EbDialog>
);

export default LiveReviewStepperModal;
